import { useCallback, useRef } from "react";

import { usePatchUserPreferencesMutation } from "@/shared/api/server/gs3Server";

import {
  shouldPersistTableUserPreferences,
  type UserTablePreferences,
} from "@/shared/utils";

/**
 * Wraps PATCH /v1/user/me/preferences so it runs only when column prefs actually
 * changed vs. what is already stored for the user on the server.
 *
 * The design-system Table calls updateUsersPreferencesOnServer on mount and on
 * navigation; this guard prevents redundant requests.
 */
export const useUpdateTableUserPreferences = (
  serverPreferences?: UserTablePreferences | null,
) => {
  const { mutate } = usePatchUserPreferencesMutation();
  const serverRef = useRef(serverPreferences);
  serverRef.current = serverPreferences;

  return useCallback(
    (next: UserTablePreferences) => {
      if (serverRef.current === undefined) {
        return;
      }

      if (!shouldPersistTableUserPreferences(next, serverRef.current)) {
        return;
      }
      if (next.tables === undefined) {
        return;
      }
      mutate({ tables: next.tables }, {
        onSuccess: ({ userPreferences }) => {
          serverRef.current = userPreferences;
        },
      });
    },
    [mutate],
  );
};
